/**
 * @fileoverview WEB-1131 (P2-1) — display casing for all-caps source titles.
 *
 * Feeds hand us all-caps titles (`SAMPLE INVITATIONAL WEEKEND`). Rendered verbatim at
 * display weight they shout over every other row. When a title is
 * {@link SHOUTING_MIN_UPPER_RATIO} or more uppercase letters it is title-cased
 * for display; known acronyms, initialisms and tokens with digits keep their
 * capitals. Anything already mixed-case is left exactly as published.
 *
 * A pure display-seam transform: never applied to stored or queried data.
 * Callers keep the raw string for the accessible name.
 */

/** Share of letters that must be uppercase before a title is recased. */
export const SHOUTING_MIN_UPPER_RATIO = 0.9;

/** Titles with fewer letters than this are never recased (`MJ`, `BTS`). */
const MIN_LETTERS = 4;

/**
 * Words that stay uppercase inside a recased title. Deliberately short:
 * place codes, leagues, formats and units that read wrong in title case.
 * Nothing that is also an ordinary word (`US`, `AM`, `LA`) — `JOIN US` must
 * become `Join Us`.
 */
const KNOWN_ACRONYMS: ReadonlySet<string> = new Set([
  'AI',
  'ATL',
  'BBQ',
  'CEO',
  'CLT',
  'CPCC',
  'DC',
  'DJ',
  'DJS',
  'EDM',
  'FC',
  'HBCU',
  'IPA',
  'LGBT',
  'LGBTQ',
  'LGBTQ+',
  'LGBTQIA+',
  'MC',
  'MLB',
  'MLS',
  'NASCAR',
  'NBA',
  'NC',
  'NCAA',
  'NFL',
  'NHL',
  'NY',
  'NYC',
  'Q&A',
  'R&B',
  'RSVP',
  'SC',
  'TBA',
  'TV',
  'UFC',
  'UK',
  'UNC',
  'UNCC',
  'USA',
  'VIP',
  'WNBA',
  'WWE',
  'YMCA',
]);

/** Lowercased mid-title (never first or last). */
const MINOR_WORDS: ReadonlySet<string> = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'but',
  'by',
  'for',
  'from',
  'in',
  'into',
  'nor',
  'of',
  'on',
  'or',
  'per',
  'the',
  'to',
  'via',
  'vs',
  'vs.',
  'with',
]);

const ROMAN_NUMERAL = /^(?=[IVX])X{0,3}(IX|IV|V?I{0,3})$/;
const LETTER = /\p{L}/gu;

/** True when ≥ 90% of the title's letters are uppercase. */
export function isShoutingTitle(title: string): boolean {
  const letters = title.match(LETTER) ?? [];
  if (letters.length < MIN_LETTERS) return false;
  const upper = letters.filter((ch) => ch !== ch.toLowerCase()).length;
  return upper / letters.length >= SHOUTING_MIN_UPPER_RATIO;
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * Cases one whitespace-free token. `isEdge` = first or last word, or the
 * start of a phrase (after a colon, or opening a quote / parenthesis) —
 * minor words are capitalised there.
 */
function caseToken(token: string, isEdge: boolean): string {
  const match = /^([^\p{L}\p{N}]*)(.*?)([^\p{L}\p{N}+]*)$/u.exec(token);
  if (!match) return token;
  const [, lead, core, trail] = match;
  if (core.length === 0) return token;
  const upper = core.toUpperCase();
  if (KNOWN_ACRONYMS.has(upper) || ROMAN_NUMERAL.test(upper) || /\d/.test(core)) {
    return token;
  }
  // `DJ'S` → `DJ's`, `CHARLOTTE'S` → `Charlotte's`.
  const possessive = /^(.+)'S$/u.exec(upper);
  if (possessive && KNOWN_ACRONYMS.has(possessive[1])) {
    return `${lead}${possessive[1]}'s${trail}`;
  }
  const lower = core.toLowerCase();
  if (!isEdge && lead.length === 0 && MINOR_WORDS.has(lower)) return `${lead}${lower}${trail}`;
  // `HIP-HOP` → `Hip-Hop`, `R&B/SOUL` keeps `R&B`.
  const cased = core
    .split(/([-/])/)
    .map((piece) =>
      piece === '-' || piece === '/' || KNOWN_ACRONYMS.has(piece.toUpperCase())
        ? piece
        : capitalize(piece),
    )
    .join('');
  return `${lead}${cased}${trail}`;
}

/**
 * Title-cases an all-caps title for display; returns every other title
 * unchanged.
 *
 * @example
 * toDisplayTitle('SAMPLE INVITATIONAL WEEKEND') // 'Sample Invitational Weekend'
 * toDisplayTitle('SAMPLE FC VS. DEMO SC')       // 'Sample FC vs. Demo SC'
 * toDisplayTitle('Demo Market Weekend')         // 'Demo Market Weekend'
 */
export function toDisplayTitle(title: string): string {
  if (!isShoutingTitle(title)) return title;
  const tokens = title.split(/(\s+)/);
  const wordPositions = tokens
    .map((token, index) => (/\S/.test(token) ? index : -1))
    .filter((index) => index >= 0);
  const first = wordPositions[0];
  const last = wordPositions[wordPositions.length - 1];
  let previousWord = '';
  return tokens
    .map((token, index) => {
      if (!/\S/.test(token)) return token;
      const opensPhrase = /[:—–]$/.test(previousWord);
      previousWord = token;
      return caseToken(token, index === first || index === last || opensPhrase);
    })
    .join('');
}
